import axios from "axios";
import { toast } from "react-toastify";
import Config from "../../Config";

const getConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
  },
});

/**
 * Fetch deals with server-side pagination and filters.
 * Query params: page, limit (per_page), search, date_from, date_to, status, sort_order (default "desc").
 * Response shape: data.data.data = { data: [...], pagination: { total, per_page, current_page, last_page, from, to, ... } }
 */
export const fetchDealsPaginated = async (
  page = 1,
  limit = 15,
  search = "",
  dateFrom = "",
  dateTo = "",
  status = "",
  sortOrder = "desc",
  sortBy = ""
) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      sort_order: sortOrder || "desc",
    });
    if (search) params.set("search", search);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (status) params.set("status", status);
    if (sortBy) params.set("sort_by", sortBy);

    const response = await axios.get(
      `${Config.baseUrl}/all/deals?${params.toString()}`,
      getConfig()
    );

    const root = response?.data?.data ?? {};
    if (root?.status_code !== "000") {
      return {
        status: false,
        data: [],
        pagination: null,
        message: root?.reason || "Failed to fetch deals",
      };
    }

    const inner = root?.data ?? {};
    const list = Array.isArray(inner?.data) ? inner.data : [];
    const pagination = inner?.pagination ?? null;

    return {
      status: true,
      data: list,
      pagination,
      message: root?.reason || "All deals retrieved successfully",
    };
  } catch (error) {
    console.error("fetchDealsPaginated error:", error);
    return {
      status: false,
      data: [],
      pagination: null,
      message:
        error?.response?.data?.data?.reason ||
        error?.response?.data?.message ||
        "Failed to fetch deals",
    };
  }
};

/**
 * Fetch deal stats (pending, approved, rejected counts) for stat cards.
 */
export const fetchDealsStats = async () => {
  try {
    const response = await axios.get(
      `${Config.baseUrl}/stats/deals`,
      getConfig()
    );
    const root = response?.data?.data ?? {};
    if (root?.status_code !== "000") {
      return { status: false, data: null, message: root?.reason };
    }
    return {
      status: true,
      data: root?.data ?? null,
      message: root?.reason,
    };
  } catch (error) {
    console.error("fetchDealsStats error:", error);
    return {
      status: false,
      data: null,
      message:
        error?.response?.data?.data?.reason ||
        error?.response?.data?.message ||
        "Failed to fetch deal stats",
    };
  }
};

/** @deprecated Use fetchDealsPaginated + fetchDealsStats instead. Kept for backwards compatibility. */
export const fetchDeals = async (setLoading, setDealsData, setError) => {
  setLoading(true);
  setError(null);
  try {
    const [dealsRes, statsRes] = await Promise.all([
      fetchDealsPaginated(1, 15, "", "", "", "", "desc"),
      fetchDealsStats(),
    ]);
    if (dealsRes.status && statsRes.status) {
      setDealsData({
        allData: dealsRes.data,
        dealsStats: statsRes.data,
      });
    } else {
      setError(true);
      throw new Error(dealsRes.message || statsRes.message || "An error occurred");
    }
  } catch (error) {
    setError(true);
    console.error(error);
  } finally {
    setLoading(false);
  }
};


export const declineDeal = async (values, dealId, {
   onSuccess,
   onError
}) => {

   try {
      console.log(values);
      console.log(dealId);
      const response = await axios.get(`${Config.baseUrl}/approve/deal/${dealId}`, values, getConfig());

      console.log(response.data);
      if (response.data.data.status_code === "000") {
         onSuccess();

         toast.success('Deal Declined!');
      } else {
         throw new Error(response.data.data.reason || 'Failed to decline deal');
      }

   } catch (error) {
      console.error(error);
      onError && onError(error);
      toast.error('Failed to Decline Deal!');

   }

}
/**
 * Fetch trashed deals with server-side pagination and filters.
 */
export const fetchTrashedDealsPaginated = async (
  page = 1,
  limit = 15,
  search = "",
  dateFrom = "",
  dateTo = "",
  sortOrder = "desc",
  sortBy = "deleted_at"
) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      sort_order: sortOrder || "desc",
    });
    if (search) params.set("search", search);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (sortBy) params.set("sort_by", sortBy);

    const response = await axios.get(
      `${Config.baseUrl}/all_trashed_deals?${params.toString()}`,
      getConfig()
    );

    const root = response?.data?.data ?? {};

    // Direct array shape: { data: [ deal, ... ] }
    if (Array.isArray(root)) {
      return {
        status: true,
        data: root,
        pagination: response?.data?.pagination ?? null,
        message: "Trashed deals retrieved successfully",
      };
    }

    // "No results" shape: { status_code: "004", data: [], reason: "No trashed deals found", ... }
    // Treat any response where root.data is a plain array as a successful empty result.
    if (Array.isArray(root?.data)) {
      return {
        status: true,
        data: root.data,
        pagination: null,
        message: root?.reason || "No trashed deals found",
      };
    }

    // Normal paginated success: { status_code: "000", data: { data: [...], pagination: {...} } }
    if (root?.status_code !== "000") {
      return {
        status: false,
        data: [],
        pagination: null,
        message: root?.reason || "Failed to fetch trashed deals",
      };
    }

    const inner = root?.data ?? {};

    // Normalise: data may arrive as an array OR as an object with numeric keys {"0":{...},"1":{...}}
    let list;
    if (Array.isArray(inner?.data)) {
      list = inner.data;
    } else if (inner?.data && typeof inner.data === "object") {
      list = Object.values(inner.data);
    } else {
      list = [];
    }

    const pagination = inner?.pagination ?? null;

    return {
      status: true,
      data: list,
      pagination,
      message: root?.reason || "Trashed deals retrieved successfully",
    };
  } catch (error) {
    console.error("fetchTrashedDealsPaginated error:", error);
    return {
      status: false,
      data: [],
      pagination: null,
      message:
        error?.response?.data?.data?.reason ||
        error?.response?.data?.message ||
        "Failed to fetch trashed deals",
    };
  }
};

/**
 * Restore a trashed deal back to its previous state.
 */
export const restoreTrashedDeal = async (dealSlug, { onSuccess, onError } = {}) => {
  try {
    const response = await axios.post(
      `${Config.baseUrl}/restore_deal/${dealSlug}`,
      {},
      getConfig()
    );
    const root = response?.data?.data ?? {};
    if (root?.status_code === "000") {
      onSuccess && onSuccess();
      toast.success("Deal restored successfully!");
    } else {
      throw new Error(root?.reason || "Failed to restore deal");
    }
  } catch (error) {
    console.error("restoreTrashedDeal error:", error);
    onError && onError(error);
    toast.error(
      error?.response?.data?.data?.reason ||
      error?.response?.data?.message ||
      "Failed to restore deal"
    );
  }
};

const failureMessage = (error, fallback) => {
  const data = error?.response?.data;
  const errors = data?.errors || data?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat()[0];
    if (first) return String(first);
  }
  return data?.data?.reason || data?.reason || data?.message || error?.message || fallback;
};

const idList = (value) => {
  if (value == null || value === "") return [];
  const number = Number(value);
  const id = Number.isFinite(number) && String(number) === String(value).trim() ? number : value;
  return [id];
};

/**
 * Assign one category and one subcategory to a deal.
 * The endpoint accepts arrays so a deal can hold more than one of each later.
 */
export const updateDealCategories = async (
  dealSlug,
  { categoryId, subcategoryId } = {},
  { onSuccess, onError } = {}
) => {
  const categoryIds = idList(categoryId);
  const subcategoryIds = idList(subcategoryId);
  if (!dealSlug || categoryIds.length === 0) {
    const message = !dealSlug ? "This deal has no slug." : "Choose a category.";
    onError && onError(new Error(message));
    toast.error(message);
    return;
  }

  try {
    const response = await axios.post(
      `${Config.baseUrl}/deals/${dealSlug}/categories`,
      {
        category_ids: categoryIds,
        subcategory_ids: subcategoryIds,
      },
      getConfig()
    );
    const root = response?.data?.data?.status_code != null ? response.data.data : response?.data ?? {};
    const code = String(root?.status_code ?? "");
    if (root?.in_error === true || !["000", "001"].includes(code)) {
      throw new Error(root?.reason || "Failed to update category");
    }
  } catch (error) {
    const message = failureMessage(error, "Failed to update category");
    console.error("updateDealCategories error:", error);
    onError && onError(new Error(message));
    toast.error(message);
    return;
  }

  toast.success("Category updated");
  onSuccess && onSuccess();
};

/**
 * Temporarily delete (archive) a deal. Moves the deal to trash; can be restored from Deal Trash Bin.
 */
export const temporarilyDeleteDeal = async (dealSlug, { onSuccess, onError } = {}) => {
  try {
    const response = await axios.post(
      `${Config.baseUrl}/temporarily_delete_deal/${dealSlug}`,
      {},
      getConfig()
    );
    const root = response?.data?.data ?? {};
    if (root?.status_code === "000") {
      onSuccess && onSuccess();
      toast.success("Deal archived successfully");
    } else {
      throw new Error(root?.reason || "Failed to archive deal");
    }
  } catch (error) {
    console.error("temporarilyDeleteDeal error:", error);
    onError && onError(error);
    toast.error(
      error?.response?.data?.data?.reason ||
      error?.response?.data?.message ||
      "Failed to archive deal"
    );
  }
};

/**
 * Permanently delete a trashed deal. This action is irreversible.
 */
export const permanentlyDeleteDeal = async (dealSlug, { onSuccess, onError } = {}) => {
  try {
    const response = await axios.delete(
      `${Config.baseUrl}/permanently_delete_deal/${dealSlug}`,
      getConfig()
    );
    const root = response?.data?.data ?? {};
    if (root?.status_code === "000") {
      onSuccess && onSuccess();
      toast.success("Deal permanently deleted");
    } else {
      throw new Error(root?.reason || "Failed to permanently delete deal");
    }
  } catch (error) {
    console.error("permanentlyDeleteDeal error:", error);
    onError && onError(error);
    toast.error(
      error?.response?.data?.data?.reason ||
      error?.response?.data?.message ||
      "Failed to permanently delete deal"
    );
  }
};

export const acceptDeal = async (values, dealId, {
   onSuccess,
   onError
}) => {

   try {
      console.log(values);
      console.log(dealId);
      const response = await axios.post(`${Config.baseUrl}/updateStatus/deal/${dealId}`, values, getConfig());

      console.log(response.data);
      if (response.data.data.status_code === "000") {
         onSuccess();

         toast.success('Deal Approved!');
      } else if (response.data.data.status_code === "998") {
         // Handle sender ID specific errors
         onError && onError({
            response: {
               data: {
                  status_code: "998",
                  message: response.data.data.reason
               }
            }
         });
         toast.error(response.data.data.reason);
      } else {
         throw new Error(response.data.data.reason || 'Failed to approve deal');
      }

   } catch (error) {
      console.error(error);
      onError && onError(error);
      toast.error('Failed to Accept Deal!');

   }

}